import { useMemo, useState } from 'react'
import { cn } from '../../lib/cn.js'
import { Textarea } from './Textarea.js'
import { ToggleGroup } from './ToggleGroup.js'

export interface MarkdownEditorProps {
  value: string
  onValueChange: (markdown: string) => void
  id?: string
  rows?: number
  placeholder?: string
  disabled?: boolean
  invalid?: boolean
  maxLength?: number
  /**
   * Nothing to type into — the wording is here to be read.
   *
   * A published legal version is never edited, only replaced, so the desk's
   * reader needs the same Markdown rendering without a Write tab or a box to
   * put a cursor in. One component that renders Markdown rather than a second
   * one that renders it almost the same way.
   */
  readOnly?: boolean
  'aria-label'?: string
  className?: string
}

/**
 * Escaped first, then the small amount of Markdown these documents actually use.
 *
 * Deliberately not a Markdown library. The preview's only job is to answer "will
 * the heading be a heading" before somebody publishes the Terms of Use, and pulling
 * in a parser to answer that means pulling in its HTML sanitising problem too. What
 * the server stores is the text; this is a look at it, not a renderer of record.
 */
function preview(markdown: string): string {
  const escaped = markdown.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  return escaped
    .replace(/^### (.*)$/gm, '<h3>$1</h3>')
    .replace(/^## (.*)$/gm, '<h2>$1</h2>')
    .replace(/^# (.*)$/gm, '<h1>$1</h1>')
    .replace(/^[-*] (.*)$/gm, '<li>$1</li>')
    .replace(/(<li>[\s\S]*?<\/li>)(?!\s*<li>)/g, '<ul>$1</ul>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
    .split(/\n{2,}/)
    .map((block) => (/^<(h\d|ul)/.test(block.trim()) ? block : `<p>${block}</p>`))
    .join('')
}

/**
 * Writing, and seeing what it will look like.
 *
 * Four documents on this platform are stored as Markdown and read by the public:
 * the Terms of Use, the Privacy Policy, the notice about health details, and the
 * professionals' terms — plus every answer in Help. A plain text box is enough to
 * *store* them and not enough to *publish* them: nobody should find out that a
 * heading was written with the wrong number of hashes after a legal document has
 * gone live and been consented to.
 *
 * Two tabs rather than side by side. The desk is used on laptops, these documents
 * are long, and half a screen each makes both halves unusable.
 */
export function MarkdownEditor({
  value,
  onValueChange,
  id,
  rows = 16,
  placeholder,
  disabled,
  invalid,
  maxLength,
  readOnly,
  className,
  ...aria
}: MarkdownEditorProps) {
  const [tab, setTab] = useState<'write' | 'preview'>(readOnly ? 'preview' : 'write')
  const html = useMemo(() => (tab === 'preview' ? preview(value) : ''), [tab, value])

  return (
    <div className={cn('flex flex-col gap-2', className)} {...aria}>
      <div className="flex items-center justify-between gap-3">
        {/* No Write tab where there is nothing to write. A disabled toggle
            beside wording somebody came to read is a control that exists only
            to say no. */}
        {!readOnly && (
          <ToggleGroup
            type="single"
            value={tab}
            onValueChange={(next) => next && setTab(next as 'write' | 'preview')}
            options={[
              { value: 'write', label: 'Write' },
              { value: 'preview', label: 'Preview' },
            ]}
          />
        )}
        {maxLength !== undefined && (
          <span className="text-caption text-ink-muted tabular-nums">
            {value.length.toLocaleString()} / {maxLength.toLocaleString()}
          </span>
        )}
      </div>

      {tab === 'write' ? (
        <Textarea
          id={id}
          rows={rows}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          invalid={invalid}
          maxLength={maxLength}
          // Monospaced while writing: Markdown is whitespace that matters, and
          // proportional type hides a list that is indented one space too far.
          className="font-mono text-second"
          onChange={(e) => onValueChange(e.target.value)}
        />
      ) : (
        <div
          className={cn(
            'markdown-preview overflow-auto rounded-md border border-hairline bg-surface p-4 text-body text-ink',
            '[&_h1]:text-title [&_h1]:font-semibold [&_h2]:text-body [&_h2]:font-semibold',
            '[&_h3]:text-second [&_h3]:font-semibold [&_p]:mb-3 [&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-5',
            '[&_a]:text-coral-fill [&_a]:underline',
          )}
          style={{ minHeight: `${rows * 1.5}rem` }}
          // The input is the desk's own typing, shown back to the desk. Nothing
          // from a user, a professional or any other account reaches this.
          dangerouslySetInnerHTML={{ __html: html }}
        />
      )}
    </div>
  )
}
